import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import { initNeo4j, closeNeo4j, checkConnection } from './config/neo4j';
import Header from './components/Header/Header';
import Home from './pages/Home/Home';
import Profile from './pages/Profile/Profile';
import EditProfile from './pages/EditProfile/EditProfile';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import Login from './pages/Login/Login';
import Signup from './pages/Signup/Signup';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import './App.css';
import logger from './utils/logger';

function App() {
  const [dbInitialized, setDbInitialized] = useState(false);
  const [dbError, setDbError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  const setupNeo4j = async () => {
    try {
     logger.log(`Attempting to initialize Neo4j (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
      await initNeo4j();
      setDbInitialized(true);
      setDbError(null);
    } catch (error) {
      logger.error('Failed to initialize Neo4j:', error);
      setDbError(error.message);
      
      if (retryCount < MAX_RETRIES - 1) {
        setRetryCount(prev => prev + 1);
      }
    }
  };

  useEffect(() => {
    setupNeo4j();

    return () => {
      closeNeo4j();
    };
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount(0);
    setDbError(null);
  };

  if (!dbInitialized && !dbError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress sx={{ color: '#2e7d32' }} />
        <div>Loading...</div>
      </Box>
    );
  }

  if (dbError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
        p={3}
        textAlign="center"
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          Database Connection Error
        </Alert>
        <p>Failed to connect to the database.</p>
        <p style={{ color: 'red', maxWidth: '600px', wordBreak: 'break-word' }}>
          {dbError}
        </p>
        {retryCount >= MAX_RETRIES && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            sx={{ mt: 2 }}
          >
            Retry Connection
          </Button>
        )}
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="app">
            <Header />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route
                  path="/profile/edit/:uid"
                  element={
                    <PrivateRoute>
                      <EditProfile />
                    </PrivateRoute>
                  }
                />
                <Route path="/profile/:uid" element={<Profile />} />
                <Route
                  path="*"
                  element={
                    <Navigate to="/" replace />
                  }
                />
              </Routes>
            </main>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
